/**
 * Planes de tratamiento: N sesiones que salen de una misma consulta.
 *
 * Cada sesión se cobra por separado y NO pasa por triaje: el paciente ya fue
 * triado en la consulta que originó el plan.
 */
import { apiFetch } from "./client";
import type { Cita } from "./citas";

export type Periodicidad = "SEMANAL" | "QUINCENAL" | "MENSUAL" | "LIBRE";

export const PERIODICIDADES: { value: Periodicidad; label: string }[] = [
  { value: "SEMANAL", label: "Semanal" },
  { value: "QUINCENAL", label: "Cada 15 días" },
  { value: "MENSUAL", label: "Mensual" },
  { value: "LIBRE", label: "Sin periodicidad fija" },
];

export interface Plan {
  id: number;
  episodio_id: number;
  paciente_id: number;
  paciente_nombre: string;
  profesional_id: number;
  profesional_nombre: string | null;
  especialidad_id: number;
  especialidad_nombre: string;
  sede_id: number;
  servicio_id: number | null;
  servicio_nombre: string | null;
  sesiones_totales: number;
  sesiones_agendadas: number;
  sesiones_atendidas: number;
  periodicidad: Periodicidad;
  estado: "ACTIVO" | "COMPLETADO" | "ANULADO";
  indicaciones: string | null;
  created_at: string;
  sesiones: Cita[];
  /** Fechas sugeridas para lo que falta. Son propuesta, no reserva. */
  fechas_propuestas: string[];
}

export interface PlanCreate {
  episodio_id: number;
  sesiones_totales: number;
  periodicidad?: Periodicidad;
  indicaciones?: string | null;
  servicio_id?: number | null;
}

export interface SesionCreate {
  /** ISO con hora. */
  inicio: string;
  duracion_min?: number | null;
  profesional_id?: number | null;
  motivo?: string | null;
}

export function crearPlan(payload: PlanCreate): Promise<Plan> {
  return apiFetch<Plan>("/v1/planes", { method: "POST", body: payload });
}

export function listPlanes(
  params: { paciente_id?: number; episodio_id?: number; por_agendar?: boolean } = {},
): Promise<Plan[]> {
  const qs = new URLSearchParams();
  if (params.paciente_id) qs.set("paciente_id", String(params.paciente_id));
  if (params.episodio_id) qs.set("episodio_id", String(params.episodio_id));
  if (params.por_agendar) qs.set("por_agendar", "true");
  const q = qs.toString();
  return apiFetch<Plan[]>(`/v1/planes${q ? `?${q}` : ""}`);
}

export function getPlan(id: number): Promise<Plan> {
  return apiFetch<Plan>(`/v1/planes/${id}`);
}

export function agendarSesion(planId: number, payload: SesionCreate): Promise<Cita> {
  return apiFetch<Cita>(`/v1/planes/${planId}/sesiones`, {
    method: "POST",
    body: payload,
  });
}

/** Cancela una sesión mal agendada. El hueco vuelve al plan. */
export function quitarSesion(planId: number, citaId: number): Promise<Plan> {
  return apiFetch<Plan>(`/v1/planes/${planId}/sesiones/${citaId}`, {
    method: "DELETE",
  });
}

export function anularPlan(id: number, motivo: string): Promise<Plan> {
  return apiFetch<Plan>(`/v1/planes/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}
